import { useTranslation } from 'react-i18next';

import { MarketplaceProject, getCategoryColor } from '@codes/marketplace/marketplace';
import { Card } from '@codes/ui-kit/components/ui/card';
import { ExternalLink, FileCode, Github } from 'lucide-react';

import { Badge, Button, cn } from '@codes/ui-kit';

interface ProjectCardProps {
    project: MarketplaceProject;
    onViewDetails: (projectId: string) => void;
}

export const ProjectCard = ({ project, onViewDetails }: ProjectCardProps) => {
    const { t } = useTranslation();

    return (
        <Card
            className={cn(
                'h-[360px] flex flex-col border border-gray-200 rounded-[18px] p-5',
                'hover:shadow-lg transition-shadow cursor-pointer'
            )}
            onClick={() => onViewDetails(project.id)}
            role="article"
            tabIndex={0}
            onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onViewDetails(project.id);
                }
            }}
            aria-label={`Project: ${project.title}`}
        >
            {/* Preview Image or Icon */}
            <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-gray-100">
                {project.previewImage ? (
                    <img
                        src={project.previewImage}
                        alt={project.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <FileCode className="w-12 h-12 text-gray-400" />
                    </div>
                )}
            </div>

            {/* Title and Description */}
            <div className="mt-4 flex-1">
                <h3 className="text-[18px] font-semibold text-gray-900 truncate">{project.title}</h3>
                <p className="text-sub text-sm mt-2 line-clamp-2">{project.description}</p>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mt-3">
                <Badge className={cn('text-xs', getCategoryColor(project.category))}>
                    {t(`marketplace.category.${project.category}`)}
                </Badge>
                {project.tags.slice(0, 2).map(tag => (
                    <Badge key={tag} variant="outline" className="text-xs">
                        {tag}
                    </Badge>
                ))}
            </div>

            {/* Footer with metadata */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                <div className="text-xs text-sub">
                    <div>{project.author}</div>
                    <div className="mt-1">v{project.version}</div>
                </div>
                <div className="flex gap-2">
                    {project.githubUrl && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="w-9 h-9 p-0"
                            onClick={e => {
                                e.stopPropagation();
                                window.open(project.githubUrl, '_blank', 'noopener,noreferrer');
                            }}
                            aria-label="View on GitHub"
                        >
                            <Github className="w-4 h-4" />
                        </Button>
                    )}
                    {project.demoUrl && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="w-9 h-9 p-0"
                            onClick={e => {
                                e.stopPropagation();
                                window.open(project.demoUrl, '_blank', 'noopener,noreferrer');
                            }}
                            aria-label="View demo"
                        >
                            <ExternalLink className="w-4 h-4" />
                        </Button>
                    )}
                </div>
            </div>
        </Card>
    );
};
